import { Label } from '@kcftech/design-system';
import {
    GroupFileAssignmentRequest,
    GroupFilesAssignmentMode,
    GroupFilesAssignmentRequest,
    GroupPictureRole,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { DefaultButton } from 'components/button/DefaultButton';
import { DocumentTitle } from 'components/DocumentTitle';
import { Detail } from 'components/detail/detail';
import { DetailsColumns, DetailsSection } from 'components/detail/styled';
import { MultiSelectOption } from 'components/forms/multiselect/MultiSelect';
import { MultiSelectTagList } from 'components/forms/multiselect/MultiSelectTagList';
import { MediaRenderer } from 'components/media/MediaRenderer';
import { DetailsPage } from 'components/page/DetailsPage';
import { InfoPopup } from 'components/popup/InfoPopup';
import { Spinner } from 'components/spinner/Spinner';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { useRouteEntities } from 'contexts/RouteEntityContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useTreeItems } from 'custom_hooks/data_hooks/tree/useTreeItems';
import { useUserDateTimeFormat } from 'custom_hooks/useUserDateTimeFormat';
import { MediaQueries } from 'custom_hooks/useWindowSize';
import { AssetMultiSelect } from 'pages/Settings/FileLibrary/FileDetails/AssetMultiSelect';
import { FileDetailsDescriptionEditor } from 'pages/Settings/FileLibrary/FileDetails/FileDetailsDescriptionEditor';
import { FileDetailsNameEditor } from 'pages/Settings/FileLibrary/FileDetails/FileDetailsNameEditor';
import { GetFileFromLibraryResult } from 'pages/Settings/FileLibrary/hooks/useFileFromLibrary';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { arraysAreSame } from 'utils/array';
import { buildTooltip } from 'utils/group_hierarchy';
import { FileBreadcrumb } from '../FileBreadcrumb';
import { PathToIdMapping } from '../hooks/useFolderIdsInPath';

export const FileDetailsColumns = styled(DetailsColumns)`
    margin-top: 2rem;
    margin-left: 48px;

    @media ${MediaQueries.xlargeAndUp} {
        grid-template-columns: minmax(min-content, auto) minmax(30rem, 1fr) minmax(
                20rem,
                1fr
            );
        grid-template-areas: 'details-section';
        grid-gap: 10%;
    }
`;

export const AssetSelectionArea = styled.div`
    display: grid;
    grid-template-columns: 1fr auto;
    column-gap: 1.5rem;
    margin-bottom: 1rem;
    max-width: 40%;
`;

export const FileDetailsSection = styled.div`
    display: flex;
    flex-direction: column;
    border-bottom: solid 1px ${tokens.border.default};
    padding: 3rem;
`;

export const SelectedAssetsSection = styled.div`
    padding: 3rem;
`;

export const FileName = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    width: fit-content;
    font-weight: 500;
    font-size: ${tokens.font.sizeMd}; ;
`;

const ImageArea = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    overflow-y: auto;
`;

const ImageContainer = styled.div`
    width: fit-content;
    padding: 3rem;
`;

const FullSizedMedia = styled(MediaRenderer)`
    height: auto;
    width: auto;
    max-height: 45rem;
    max-width: 100%;
`;

type FileDetailsPageProps = Readonly<{
    file: GetFileFromLibraryResult | undefined;
    isLoading: boolean;
    refetch: () => void;
    folderHierarchy: PathToIdMapping[] | undefined;
    refreshTable: () => void;
    resetPaging: () => void;
}>;

export function FileDetailsPage({
    file,
    isLoading: isFileLoading,
    refetch,
    folderHierarchy,
    refreshTable,
    resetPaging,
}: FileDetailsPageProps) {
    const { showToast } = useNotificationContext();
    const { canEditFileDetails } = usePermissions();

    const { formatDateTime } = useUserDateTimeFormat();

    const [newSelectedAssets, setNewSelectedAssets] = useState<
        MultiSelectOption<string>[]
    >([]);
    const [initialSelectedAssets, setInitialSelectedAssets] = useState<
        MultiSelectOption<string>[]
    >([]);
    const { corporation } = useRouteEntities();
    const associatedAssetIds = useMemo(() => file?.GroupIds ?? [], [file]);
    const disableSaveButton = useMemo(
        () =>
            arraysAreSame(
                initialSelectedAssets.map((ia) => ia.value),
                newSelectedAssets.map((na) => na.value),
            ),
        [initialSelectedAssets, newSelectedAssets],
    );
    // We are using the TreeItems hook temporarily until we refactor the files api to
    // return an array of GroupChildren instead of GroupIds so that we have the asset names
    const { data: fileAssets, isLoading: isAssetsLoading } =
        useTreeItems(associatedAssetIds);

    const { mutate: saveFileAssets } = useMutation({
        mutationFn: async () => {
            if (!file) {
                throw new Error('Missing file');
            }
            const newSelectedAssetsSet = new Set(newSelectedAssets.map((sa) => sa.value));
            const initialSelectedAssetsSet = new Set(
                initialSelectedAssets.map((sa) => sa.value),
            );
            const assetsToAssign = [
                ...newSelectedAssetsSet.difference(initialSelectedAssetsSet),
            ];
            const assetsToRemove = [
                ...initialSelectedAssetsSet.difference(newSelectedAssetsSet),
            ];
            await sdClient.assignFilesToGroups({
                body: new GroupFilesAssignmentRequest({
                    Mode: GroupFilesAssignmentMode.Append,
                    Assignments: assetsToAssign.map(
                        (groupId) =>
                            new GroupFileAssignmentRequest({
                                GroupId: groupId,
                                PictureId: file.Id,
                                Role: GroupPictureRole.Other,
                            }),
                    ),
                }),
            });

            await sdClient.assignFilesToGroups({
                body: new GroupFilesAssignmentRequest({
                    Mode: GroupFilesAssignmentMode.Remove,
                    Assignments: assetsToRemove.map(
                        (groupId) =>
                            new GroupFileAssignmentRequest({
                                GroupId: groupId,
                                PictureId: file.Id,
                                Role: GroupPictureRole.Other,
                            }),
                    ),
                }),
            });
        },

        onSuccess: () => {
            showToast({
                type: 'success',
                content: 'Successfully updated asset assignments.',
            });
            setInitialSelectedAssets(newSelectedAssets);
        },

        onError: (_e) => {
            showToast({
                type: 'error',
                content: 'An error occurred when updating asset assignments.',
            });
        },
    });

    useEffect(() => {
        setNewSelectedAssets(
            fileAssets?.map((fa) => ({
                title: buildTooltip(fa.Hierarchy, undefined, fa.Name),
                value: fa.Id,
            })) ?? [],
        );
        setInitialSelectedAssets(
            fileAssets?.map((fa) => ({
                title: buildTooltip(fa.Hierarchy, undefined, fa.Name),
                value: fa.Id,
            })) ?? [],
        );
    }, [fileAssets]);

    const isLoading = isFileLoading || isAssetsLoading;
    const hasError = !file && !isLoading;

    return (
        <DetailsPage
            isLoading={isLoading}
            error={hasError ? 'No file data' : undefined}
            invalidURL={hasError}
            fullHeight
            fullWidth
        >
            <DocumentTitle title={`${file?.Name ?? ''} File Details`} />
            {file && (
                <>
                    <FileDetailsSection>
                        {folderHierarchy && (
                            <FileBreadcrumb
                                paths={folderHierarchy}
                                resetPaging={resetPaging}
                            />
                        )}
                        <FileName className="ml-10 mt-10">
                            <FileDetailsNameEditor
                                canEdit={canEditFileDetails}
                                file={file}
                                refetchFile={refetch}
                                refreshTable={refreshTable}
                            />
                        </FileName>
                        <FileDetailsColumns>
                            <DetailsSection>
                                <Detail name="Description">
                                    <FileDetailsDescriptionEditor
                                        canEdit={canEditFileDetails}
                                        file={file}
                                        refetchFile={refetch}
                                        refreshTable={refreshTable}
                                    />
                                </Detail>
                                <Detail name="Resolution">
                                    {file.ImageMetadata?.Width}
                                    <span className="mx-5">x</span>
                                    {file.ImageMetadata?.Height}
                                </Detail>
                            </DetailsSection>
                            <DetailsSection>
                                <Detail name="Uploaded">
                                    {formatDateTime(file.CreationDate)}
                                </Detail>
                                <Detail name="Last Update">
                                    {formatDateTime(file.UpdateDate)}
                                </Detail>
                            </DetailsSection>
                        </FileDetailsColumns>
                    </FileDetailsSection>
                    <SelectedAssetsSection>
                        {file.LocationId ? (
                            <DetailsSection>
                                {canEditFileDetails ? (
                                    <>
                                        <AssetSelectionArea>
                                            <AssetMultiSelect
                                                corporationId={corporation?.Id ?? ''}
                                                locationId={file.LocationId}
                                                selectedAssets={newSelectedAssets}
                                                onChange={setNewSelectedAssets}
                                            />
                                            <DefaultButton
                                                title="Save Assets to File"
                                                disabled={disableSaveButton}
                                                onClick={() => saveFileAssets()}
                                            >
                                                Save Assets
                                            </DefaultButton>
                                        </AssetSelectionArea>
                                        <MultiSelectTagList
                                            selectedOptions={newSelectedAssets}
                                            onChange={setNewSelectedAssets}
                                        />
                                    </>
                                ) : (
                                    <Detail name="Assets">
                                        {newSelectedAssets.map((a) => a.title).join(', ')}
                                    </Detail>
                                )}
                            </DetailsSection>
                        ) : (
                            <div className="flex-row align-center">
                                <Label label="Assets" className="mt-1" />
                                <InfoPopup title="Assets">
                                    <p>Only location images can be linked to assets.</p>
                                </InfoPopup>
                            </div>
                        )}
                    </SelectedAssetsSection>
                    <ImageArea>
                        <ImageContainer>
                            <FullSizedMedia
                                fileType={file.EntryType}
                                fileName={file.Name}
                                storageKey={file.StorageFileName}
                                mediaType={file.MediaType}
                                pending={<Spinner className="pr-15" />}
                            />
                        </ImageContainer>
                    </ImageArea>
                </>
            )}
        </DetailsPage>
    );
}
